import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGridPro, GridInputRowSelectionModel, GridColDef } from '@mui/x-data-grid-pro';
import { SubMenu } from './SubMenu';
import { useTranslation } from 'react-i18next';
import { TimekeeperService } from 'services/TimekeeperService';
import { Limit } from 'repository/timekeeper/@types/Limit';
import { TipoLimiteEnum } from 'repository/timekeeper/@types/TypeLimitEnum';
import { TipoCheckLimitEnum } from 'repository/timekeeper/@types/TypeCheckLimitEnum';
import { TypeShiftLimitEnum } from 'repository/timekeeper/@types/TypeShiftLimitEnum';

//import { PatternStartDateEnum } from 'repository/timekeeper/@types/TypePatternStartDate';



const LimitsTable = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedLimits, setSelectedLimits] = useState<GridInputRowSelectionModel | undefined>([]);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [selected, setSelected] = useState<any>([]);

    const timekeeperService = useMemo(() => new TimekeeperService(), []);

    const columns: GridColDef[] = [
        { field: 'name', headerName: t("accruals.limits.table.columns.name"), flex: 1 },
        { field: 'type_limit', headerName: t("accruals.limits.table.columns.limit_type"), flex: 1, headerAlign: 'center', align: 'center', filterable: false },
        { field: 'type_check_limit', headerName: t("accruals.limits.table.columns.limit"), flex: 1, headerAlign: 'center', align: 'center', filterable: false},
        { field: 'dates', headerName: t("accruals.limits.table.columns.dates"), flex: 1, headerAlign: 'center', align: 'center', filterable: false },
    ];

    const get_type_limit_options = useCallback((type_limit: TipoLimiteEnum) => {
        const options: Record<TipoLimiteEnum, string> = {
            "CANTIDAD_GANANCIA": t("accruals.limits.dialog.inputs.option_type_limit.profit_amount_limit"),
            "BALANCE_GANANCIA": t("accruals.limits.dialog.inputs.option_type_limit.accumulation_displacement_limit"),
            "DESPLAZAMIENTO_ACUMULACION": t("accruals.limits.dialog.inputs.option_type_limit.profit_balance_limit"),
            "HORAS_ACUMULADAS": t("accruals.limits.dialog.inputs.option_type_limit.accumulated_hours_limit_minus_the_use_of_accumulation_balances"),
            "USO_BALANCES": t("accruals.limits.dialog.inputs.option_type_limit.limit_on_the_use_of_balances"),
        };
        return options[type_limit];
    }, [t]);

    const get_limits = useCallback((limit: TipoCheckLimitEnum | TypeShiftLimitEnum) => {
        const limits: Record<string, string> = {
            "COMPROBAR LIMITE DURANTE TEMPORADA": t("accruals.limits.dialog.inputs.option_type_check_limit.check_limit_during_season"),
            "COMPROBAR LIMITE DURANTE CONTINUO": t("accruals.limits.dialog.inputs.option_type_check_limit.check_limit_during_continuous_operation"),
            "COMPROBAR LIMITE EN PATRON": t("accruals.limits.dialog.inputs.option_type_check_limit.check_limit_in_pattern"),
            "MAXIMO": t("accruals.limits.dialog.inputs.options_type_shift_limit.maximum"),
            "MAXIMO_PERMITIR_BALANCES_NEGATIVO": t("accruals.limits.dialog.inputs.options_type_shift_limit.maximum_allowed_negative_balances"),
            "MINIMO": t("accruals.limits.dialog.inputs.options_type_shift_limit.minimum"),
        };
        return limits[limit];
    }, [t]);

    const reloading = useCallback(() => {
        setLoading(true);
        timekeeperService.limits.getAll({include: 'date_pattern'})
            .then((response: Limit[]) => {
                const date_pattern = response.map((data) => {
                    const checkLimit = 'type_check_limit' in data
                        ? data.type_check_limit
                        : ('type_shift_limit' in data ? data.type_shift_limit : '-');

                    return {
                        id: data.limit_id,
                        name: data.name,
                        type_limit: data.type_limit ? get_type_limit_options(data.type_limit) : '-',
                        type_check_limit: get_limits(checkLimit as TipoCheckLimitEnum | TypeShiftLimitEnum),
                        dates: data.date_pattern ? data.date_pattern.name : '-'
                    };
                });
                setRows(date_pattern);
            })
            .catch((error) => {
                console.error('Error al obtener los patrones de fecha:', error);
                setRows([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [timekeeperService, get_type_limit_options, get_limits]);

    useEffect(() => {
        reloading();
    }, [reloading]);

    const cleanSelected = () => {
        setSelected([]);
        setSelectedLimits([]);
    }

    return (
        <div style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null)
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                loading={loading}
                rowSelectionModel={selectedLimits}
                pagination
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    let row = rows.filter((row) => newRowSelectionModel.includes(row.id))
                    setSelectedLimits(newRowSelectionModel);
                    setSelected(row);

                }}
                checkboxSelection
                localeText={
                    {
                        headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                        headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                        headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                        headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                        headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                        headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                        footerRowSelected: (count) =>
                            count > 1 ? `${count.toLocaleString()} filas seleccionadas`
                                : `${count.toLocaleString()} fila seleccionada`,

                        footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                        MuiTablePagination: {
                            labelDisplayedRows: ({ from, to, count }) =>
                                `${from} - ${to} of ${count === -1 ? `more than ${to}` : count}`,
                            labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                        },
                    }
                }
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnMenu
                headerFilters
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    }
                }}
            />
            <SubMenu
                selected={selected}
                cleanSelected={cleanSelected}
                rows={rows}
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                reloading={reloading}
            />
        </div>
    );
}

export { LimitsTable };